import { IconButton, Tag } from '@ads/components-react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { CountryLangViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

export interface CountryLangTableProps {
  /** Página atual de idiomas já fatiada para exibição. */
  readonly data: readonly CountryLangViewModel[];
  readonly isLoading: boolean;
  /** Página atual (1-indexed). */
  readonly currentPage: number;
  /** Total de páginas (derivado client-side do total filtrado). */
  readonly totalPages: number;
  /** Total de registros filtrados (para o texto "Exibindo X–Y de Z"). */
  readonly totalCount: number;
  /** Itens por página. */
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly emptyStateAction?: ReactNode;
  readonly onEdit: (lang: CountryLangViewModel) => void;
  readonly onDelete: (lang: CountryLangViewModel) => void;
}

/**
 * Tabela de Idiomas do país. Renderiza Idioma, Cultura (Tag), Nome, Ativo (Tag) e
 * ações (editar/remover), delegando loading/empty/paginação ao `DataTable` e
 * habilitando ordenação client-side. Fiel ao frame *Screen: Idiomas do país
 * (Listagem)* de `design/location.pen`.
 *
 * TODO: a paginação é client-side (a lista chega completa da API). Quando o
 * backend paginar `GET /api/v1/location/countries/{countryId}/langs`, usar
 * `totalPages`/`totalCount` do envelope. Ver `COUNTRY_LANG_PAGE_SIZE`.
 */
export function CountryLangTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  emptyStateAction,
  onEdit,
  onDelete,
}: CountryLangTableProps) {
  const { t } = useTranslation('location');

  const columns = useMemo<ColumnDef<CountryLangViewModel, unknown>[]>(
    () => [
      {
        id: 'language',
        accessorKey: 'cultureDisplayName',
        header: t('countryLangs.table.columns.language'),
        cell: info => (
          <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
            {(info.getValue() as string) || '-'}
          </span>
        ),
      },
      {
        id: 'culture',
        accessorKey: 'cultureInfo',
        header: t('countryLangs.table.columns.culture'),
        cell: ({ row }) => (
          <Tag label={row.original.cultureInfo ?? ''} size="md" custom={{ text: 'text-nowrap' }} />
        ),
      },
      {
        accessorKey: 'name',
        header: t('countryLangs.table.columns.name'),
        cell: info => <span className="ts-paragraph-xxs">{(info.getValue() as string) ?? ''}</span>,
      },
      {
        id: 'active',
        accessorKey: 'active',
        header: t('countryLangs.table.columns.active'),
        cell: ({ row }) => {
          const active = row.original.active ?? false;
          return (
            <Tag
              label={t(
                active ? 'countryLangs.table.statusActive' : 'countryLangs.table.statusInactive',
              )}
              size="md"
              status={active ? 'positive' : 'default'}
              custom={{ text: 'text-nowrap' }}
            />
          );
        },
      },
      {
        id: 'actions',
        header: () => <div className="text-right">{t('countryLangs.table.columns.actions')}</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-quark">
            <IconButton
              icon={PencilSquareIcon}
              ariaLabel={t('countryLangs.table.edit')}
              hierarchy="tertiary"
              size="md"
              onClick={() => onEdit(row.original)}
            />
            <IconButton
              icon={TrashIcon}
              ariaLabel={t('countryLangs.table.delete')}
              hierarchy="tertiary"
              className="!text-base-danger-600"
              size="md"
              onClick={() => onDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [t, onEdit, onDelete],
  );

  return (
    <DataTable
      data={data as CountryLangViewModel[]}
      columns={columns}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      onPageChange={onPageChange}
      emptyStateMessage={t('countryLangs.table.empty')}
      emptyStateDescription={t('countryLangs.table.emptyDescription')}
      {...(emptyStateAction ? { emptyStateAction } : {})}
      enableSorting
    />
  );
}
